import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Heart, Briefcase, Coins, Activity, Users, Home, ArrowRight } from 'lucide-react';
import { PremiumButton, SectionTitle, FlowerMotif } from './premium/Decor';

const PROBLEMS = [
  { icon: Heart, hi: 'प्रेम, विवाह और रिश्ते', en: 'Love, Marriage & Relationships',
    pHi: 'क्या आपका दिल बार-बार टूटता है? शादी में देरी? वैवाहिक जीवन में खुशी नहीं?',
    pEn: 'Heartbreaks repeating? Marriage delayed? No happiness in marriage?' },
  { icon: Briefcase, hi: 'करियर, नौकरी और व्यापार', en: 'Career, Job & Business',
    pHi: 'क्या नौकरी में तरक्की रुकी है? प्रमोशन नहीं? बिजनेस में नुकसान?',
    pEn: 'Career stuck? No promotions? Business losses?' },
  { icon: Coins, hi: 'धन, संपत्ति और समृद्धि', en: 'Wealth, Property & Prosperity',
    pHi: 'पैसा आता है पर टिकता नहीं? कर्ज बढ़ रहा है? निवेश में नुकसान?',
    pEn: 'Money comes but slips away? Mounting debts? Losses in investments?' },
  { icon: Activity, hi: 'स्वास्थ्य और जीवन शक्ति', en: 'Health & Vitality',
    pHi: 'बार-बार बीमार पड़ रहे हैं? तनाव और अनिद्रा? पुरानी बीमारी ठीक नहीं हो रही?',
    pEn: 'Falling ill repeatedly? Stress and insomnia? Chronic illness not healing?' },
  { icon: Users, hi: 'परिवार और संबंध', en: 'Family & Bonds',
    pHi: 'परिवार में कलह? संतान सुख नहीं? रिश्ते बिगड़ रहे हैं?',
    pEn: 'Family conflicts? No child happiness? Relations breaking?' },
  { icon: Home, hi: 'भविष्य और जीवन दिशा', en: 'Future & Life Direction',
    pHi: 'जीवन का उद्देश्य समझ नहीं आ रहा? आगे क्या करें पता नहीं?',
    pEn: "Can't find life's purpose? Don't know what to do next?" },
];

const ProblemSection = ({ onOpenForm }) => {
  const { language } = useLanguage();
  const isHi = language === 'hi';

  return (
    <section className="relative bg-brand-cream py-16 md:py-24 overflow-hidden">
      <div className="absolute inset-0 bg-mandala-watermark bg-repeat opacity-60 pointer-events-none" />
      <div className="absolute -top-32 right-1/4 w-[420px] h-[420px] bg-mandala-large bg-no-repeat bg-center opacity-35 pointer-events-none" aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionTitle
          eyebrow={isHi ? 'जीवन की चुनौतियाँ' : 'Life Challenges'}
          title={isHi ? 'क्या आप भी इन समस्याओं से जूझ रहे हैं?' : 'Are You Struggling With These Issues?'}
          subtitle={isHi
            ? 'हर समस्या का जवाब आपकी जन्म कुंडली में छिपा है — सिर्फ़ सही दिशा चाहिए।'
            : 'Every problem has its answer hidden in your birth chart — you just need the right guidance.'}
          language={language}
        />

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mt-12">
          {PROBLEMS.map((p, i) => {
            const Icon = p.icon;
            return (
              <div
                key={i}
                className="group relative bg-white rounded-2xl border border-brand-gold/25 p-6 md:p-7 shadow-sm hover:shadow-gold-glow hover:-translate-y-1 transition-all duration-300 opacity-0 animate-fade-up"
                style={{ animationDelay: `${i * 90}ms` }}
              >
                <div className="absolute top-3 right-3 opacity-30 group-hover:opacity-60 transition-opacity">
                  <FlowerMotif className="w-7 h-7 text-brand-gold" />
                </div>
                <div className="w-14 h-14 rounded-2xl bg-brand-maroon/10 text-brand-maroon flex items-center justify-center mb-4 group-hover:bg-brand-maroon group-hover:text-white transition-colors">
                  <Icon className="w-6 h-6" />
                </div>
                <h3 className={`font-playfair font-bold text-xl text-brand-maroon mb-3 ${isHi ? 'font-hindi-serif' : ''}`}>
                  {isHi ? p.hi : p.en}
                </h3>
                <p className={`text-brand-text-soft text-sm md:text-base leading-relaxed ${isHi ? 'font-hindi' : 'font-outfit'}`}>
                  {isHi ? p.pHi : p.pEn}
                </p>
              </div>
            );
          })}
        </div>

        <div className="mt-14 text-center">
          <p className={`text-brand-text-soft mb-5 ${isHi ? 'font-hindi' : 'font-outfit'}`}>
            {isHi ? 'अभी अपनी कुंडली से सटीक उत्तर पाएं' : 'Get accurate answers from your Kundli now'}
          </p>
          <PremiumButton size="lg" onClick={onOpenForm}>
            <span className={isHi ? 'font-hindi' : 'font-outfit'}>
              {isHi ? 'अभी समाधान जानें — सिर्फ ₹99' : 'Get My Solutions — Only ₹99'}
            </span>
            <ArrowRight className="w-4 h-4" />
          </PremiumButton>
        </div>
      </div>
    </section>
  );
};

export default ProblemSection;
